import React from 'react';
import { Plus, Minus, Check, Droplet, Mic, Lightbulb, Cable, Sliders, Zap, Package, Star, Box, Image as ImageIcon, ExternalLink } from 'lucide-react';
import { Product, ProductAddon as ProductAddonType } from '../types';
import { Link } from 'react-router-dom';


interface AddonQuantitySelectorProps {
  productAddons: ProductAddonType[];
  selectedAddons: { [key: string]: number };
  onAddonsChange: (addons: { [key: string]: number }) => void;
  className?: string;
}

const ICONS: { [key: string]: React.FC<any> } = {
  droplet: Droplet,
  mic: Mic,
  lightbulb: Lightbulb,
  cable: Cable,
  sliders: Sliders,
  zap: Zap,
  package: Package
};

const AddonQuantitySelector: React.FC<AddonQuantitySelectorProps> = ({
  productAddons,
  selectedAddons,
  onAddonsChange,
  className = ''
}) => {
  if (!productAddons || productAddons.length === 0) {
    return null;
  }

  const getIcon = (iconName?: string) => {
    if (!iconName || !ICONS[iconName]) return <Package size={20} />;
    const IconComponent = ICONS[iconName];
    return <IconComponent size={20} />;
  };

  const handleQuantityChange = (addonId: string, delta: number, maxQty: number) => {
    const currentQty = selectedAddons[addonId] || 0;
    const newQty = Math.max(0, Math.min(maxQty, currentQty + delta));

    const newAddons = { ...selectedAddons };
    if (newQty === 0) {
      delete newAddons[addonId];
    } else {
      newAddons[addonId] = newQty;
    }

    onAddonsChange(newAddons);
  };

  const handleToggle = (addonId: string) => {
    const newAddons = { ...selectedAddons };
    if (selectedAddons[addonId]) {
      delete newAddons[addonId];
    } else {
      newAddons[addonId] = 1;
    }
    onAddonsChange(newAddons);
  };

  const getPrice = (addon: ProductAddonType) => {
    if (addon.custom_price !== undefined && addon.custom_price !== null) {
      return addon.custom_price;
    }
    return parseFloat(addon.effective_price || '0');
  };

  const getAddonId = (addon: ProductAddonType): string => {
    return addon.id;
  };

  // Group addons by category
  const groupedAddons: { [key: string]: ProductAddonType[] } = {};
  productAddons.forEach(addon => {
    const category = addon.template_category || addon.product_category || 'Övrigt';
    if (!groupedAddons[category]) {
      groupedAddons[category] = [];
    }
    groupedAddons[category].push(addon);
  });

  return (
    <div className={className}>
      <h3 className="font-bold mb-3 text-white">Tillval och uppgraderingar</h3>

      <div className="space-y-4">
        {Object.entries(groupedAddons).map(([category, addons]) => (
          <div key={category}>
            <h4 className="text-sm font-semibold text-gray-400 mb-2 uppercase tracking-wider">
              {category}
            </h4>
            <div className="space-y-2">
              {addons.map(addon => {
                const isTemplate = !!addon.addon_template_id;
                const addonId = getAddonId(addon);
                const quantity = selectedAddons[addonId] || 0;
                const price = getPrice(addon);
                const isSelected = quantity > 0;

                const displayName = addon.template_name || addon.product_name || '';
                const displayDescription = addon.template_description || addon.product_short_description || '';
                const isPopular = addon.template_is_popular || addon.product_popular || false;
                const quantityType = addon.template_quantity_type || 'fixed';
                const maxQuantity = addon.custom_quantity_settings?.max_quantity || addon.template_max_quantity || 10;
                const unitLabel = addon.template_unit_label || 'st';
                const productImage = addon.product_image;
                const productSlug = addon.product_slug;
                const productId = addon.addon_product_id;
                const templateIcon = addon.template_icon;

                return (
                  <div
                    key={addonId}
                    className={`rounded-lg p-3 transition-all ${
                      isSelected
                        ? 'bg-purple-900/40 border border-purple-500'
                        : 'bg-gray-800/50 border border-gray-700 hover:border-gray-600'
                    }`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        {isTemplate ? (
                          <div className={`flex-shrink-0 ${isSelected ? 'text-purple-400' : 'text-gray-400'}`}>
                            {getIcon(templateIcon)}
                          </div>
                        ) : productImage ? (
                          <img
                            src={productImage}
                            alt={displayName}
                            className="w-12 h-12 flex-shrink-0 rounded object-cover border border-gray-600"
                          />
                        ) : (
                          <div className="w-12 h-12 flex-shrink-0 bg-gray-700 rounded flex items-center justify-center border border-gray-600">
                            <ImageIcon size={20} className="text-gray-400" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 mb-0.5">
                            {!isTemplate && productId ? (
                              <Link
                                to={`/produkter/${productSlug || productId}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="font-medium text-white hover:text-purple-400 transition-colors inline-flex items-center gap-1 group truncate"
                              >
                                {displayName}
                                <ExternalLink size={12} className="opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                              </Link>
                            ) : (
                              <span className="font-medium text-white truncate">{displayName}</span>
                            )}
                            {isPopular && (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-yellow-500/20 text-yellow-400 border border-yellow-500/30 flex-shrink-0">
                                <Star size={10} className="fill-current" />
                              </span>
                            )}
                            {addon.is_recommended && (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-green-500/20 text-green-400 border border-green-500/30 flex-shrink-0">
                                Rekommenderat
                              </span>
                            )}
                          </div>
                          {displayDescription && (
                            <p className="text-xs text-gray-400 mb-1 line-clamp-1">{displayDescription}</p>
                          )}
                          <div className="text-sm">
                            <span className="font-medium text-white">{price} kr</span>
                            <span className="text-gray-400 text-xs ml-1">
                              {isTemplate && addon.template_pricing_type !== 'fixed' ? `/ ${unitLabel}` : '/ dag'}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="flex-shrink-0">
                        {quantityType === 'fixed' ? (
                          <label className="flex items-center cursor-pointer">
                            <div
                              className={`w-10 h-10 rounded-none border flex items-center justify-center transition-all ${
                                isSelected
                                  ? 'bg-purple-600 border-purple-500'
                                  : 'bg-gray-700 border-gray-600 hover:border-purple-500'
                              }`}
                              onClick={() => handleToggle(addonId)}
                            >
                              {isSelected && <Check className="text-white" size={20} />}
                            </div>
                          </label>
                        ) : (
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => handleQuantityChange(addonId, -1, maxQuantity)}
                              disabled={quantity <= 0}
                              className={`w-8 h-8 rounded flex items-center justify-center transition-all ${
                                quantity > 0
                                  ? 'bg-gray-700 hover:bg-gray-600 text-white'
                                  : 'bg-gray-800 text-gray-600 cursor-not-allowed'
                              }`}
                            >
                              <Minus size={16} />
                            </button>
                            <div className="w-12 text-center">
                              <div className="text-xl font-bold text-white">{quantity}</div>
                              <div className="text-xs text-gray-500">{unitLabel}</div>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleQuantityChange(addonId, 1, maxQuantity)}
                              disabled={quantity >= maxQuantity}
                              className={`w-8 h-8 rounded flex items-center justify-center transition-all ${
                                quantity < maxQuantity
                                  ? 'bg-purple-600 hover:bg-purple-700 text-white'
                                  : 'bg-gray-800 text-gray-600 cursor-not-allowed'
                              }`}
                            >
                              <Plus size={16} />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>

                    {isSelected && quantity > 1 && quantityType === 'selectable' && (
                      <div className="mt-3 pt-3 border-t border-gray-700">
                        <div className="flex justify-between text-sm">
                          <span className="text-white">Delsumma ({quantity} × {price} kr):</span>
                          <span className="font-medium text-white">{quantity * price} kr</span>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AddonQuantitySelector;
